import React, { useContext, useState } from 'react';
import { Row, Col, Container, Accordion, Badge, useAccordionButton, AccordionContext } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import classNames from 'classnames';

// import seo from '../../../assets/images/Company2.jpeg';
import seo from '../../../assets/images/hero/New folder/Home/Company2.jpeg'
import ContactUS from '../../../assets/images/ContactUS.jpg'
import { colors } from '../../../styles/colors';
import { Zap, Target, TrendingUp, Shield } from "lucide-react";

// types
import { Feature, CrmFeatures } from './types';

// images
type FeaturesProps = {
    features: Feature[];
    crmFeatures: CrmFeatures[]
    containerClass?: string;
};

type CustomToggleProps = {
    children: React.ReactNode;
    eventKey: string;
    containerClass: string;
    linkClass: string;
    callback?: (eventKey: string) => void;
};

const CustomToggle = ({ children, eventKey, linkClass, callback }: CustomToggleProps) => {
    const { activeEventKey } = useContext(AccordionContext);
    const decoratedOnClick = useAccordionButton(eventKey, () => callback && callback(eventKey));
    const isCurrentEventKey = activeEventKey === eventKey;

    return (
        <Link
            to="#"
            className={classNames(linkClass, {
                collapsed: !isCurrentEventKey,
            })}
            onClick={decoratedOnClick}
        >
            {children}
        </Link>
    );
};

const benefits = [
    {
        icon: "zap",
        title: "Boost Productivity",
        desc: "Reduce manual work with powerful automation.",
    },
    {
        icon: "trending-up",
        title: "Increase Conversions",
        desc: "Get better insights into what drives deals.",
    },
    {
        icon: "users",
        title: "Better Team Collaboration",
        desc: "Share dashboards and goals with your team.",
    },
    {
        icon: "dollar-sign",
        title: "Grow Revenue Faster",
        desc: "Focus on deals that matter with accurate data.",
    },
];

const whyBetterFeatures = [
    {
        icon: Zap,
        title: "Zero learning curve",
        desc: "Get your team up and running in minutes with an intuitive interface designed for real-world sales workflows.",
    },
    {
        icon: Target,
        title: "Powerful automation & reminders",
        desc: "Never miss a follow-up with intelligent automation that keeps deals moving forward automatically.",
    },
    {
        icon: TrendingUp,
        title: "Clean design — easy adoption",
        desc: "A modern, clutter-free interface your sales team will actually enjoy using every day.",
    },
    {
        icon: Shield,
        title: "Secure, reliable & scalable",
        desc: "Enterprise-grade security and infrastructure that scales effortlessly as your business grows.",
    },
];

const Features1 = ({ features, crmFeatures, containerClass }: FeaturesProps) => {

    const [currentSlide, setCurrentSlide] = useState(0);

    const nextSlide = () =>
        setCurrentSlide((prev) => (prev + 1) % whyBetterFeatures.length);

    const prevSlide = () =>
        setCurrentSlide((prev) => (prev - 1 + whyBetterFeatures.length) % whyBetterFeatures.length);

    return (
        <>
            <section className={containerClass}>
                <Container>
                    <Row className="mt-1">
                        <Row className="justify-content-center">
                            <Col className="text-center">
                                {/* <h2 className="fw-semibold mb-4">All-in-One CRM Stack</h2> */}
                                <h2
                                    style={{
                                        fontSize: "48px",
                                        fontWeight: 700,
                                        color: colors.primary.darkBlue,
                                        // WebkitBackgroundClip: "text",
                                        // WebkitTextFillColor: "transparent",
                                    }}
                                >
                                    All-in-One CRM to Keep Your Sales on Track
                                </h2>
                                <p className="text-muted mb-5">SalesTrackify gives your team everything they need to capture leads, follow up on opportunities and close more deals – all from one central platform.</p>
                            </Col>
                        </Row>

                        {crmFeatures.map((crm, index) => (
                            <Col
                                lg={4}
                                md={6}
                                sm={12}
                                key={index}
                                className="mb-4"
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                            >
                                <div
                                    className="p-4 bg-white rounded-4 h-100 text-start shadow-sm transition hover-shadow"

                                    style={{
                                        border: "2px solid #e5e7eb",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        transition: "all 0.3s ease",
                                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                        transform: "translateY(0)", // default
                                    }}
                                    onMouseEnter={(e) => {
                                        // Card lift effect (hover:-translate-y-2)
                                        e.currentTarget.style.transform = "translateY(-8px)";

                                        // Border + shadow like Tailwind hover
                                        e.currentTarget.style.borderColor = "#2dd4bf";
                                        e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";

                                        // Title color change
                                        const title = e.currentTarget.querySelector("h5");
                                        if (title) title.style.color = "#2dd4bf";
                                    }}
                                    onMouseLeave={(e) => {
                                        // Reset Y movement
                                        e.currentTarget.style.transform = "translateY(0)";

                                        // Reset styles
                                        e.currentTarget.style.borderColor = "#e5e7eb";
                                        e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";

                                        // Reset title color
                                        const title = e.currentTarget.querySelector("h5");
                                        if (title) title.style.color = "#1e3a8a";
                                    }}
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        <div className="me-3 fs-3">{crm.icon}</div>

                                        <h5
                                            className="mb-0 fw-bold"
                                            style={{
                                                color: "#1e3a8a",
                                                transition: "color 0.3s ease",
                                            }}
                                        >
                                            {crm.title}
                                        </h5>
                                    </div>

                                    <ul className="list-unstyled mt-3 mb-0 text-muted small">
                                        {crm.features.map((feat, idx) => (
                                            <li key={idx} className="mb-2 d-flex align-items-start">
                                                <FeatherIcon icon="check-circle" size={14} className="me-2" style={{ color: "#2dd4bf" }} />
                                                {feat}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            <section style={{
                background: "linear-gradient(135deg, #24347D 0%, #26A2AB 100%)"
            }}>
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-9 lg:px-8">
                    <div className="grid items-center gap-10 lg:grid-cols-2">

                        {/* Left Content */}
                        <div>
                            <h2 className="text-3xl text-white  font-extrabold tracking-tight">
                                Real-time sales insights at your fingertips
                            </h2>

                            <p className="mt-3 text-white/75">
                                Identify what’s working, spot risks early, and coach your team
                                with clear metrics and dashboards.
                            </p>

                            <ul className="mt-4 space-y-3 text-white/85">

                                <li className="flex items-start gap-3">
                                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-teal-400">
                                        ✓
                                    </span>
                                    <span>Track pipeline and revenue in real time</span>
                                </li>

                                <li className="flex items-start gap-3">
                                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-teal-400">
                                        ✓
                                    </span>
                                    <span>Monitor rep performance and activity</span>
                                </li>

                                <li className="flex items-start gap-3">
                                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-teal-400">
                                        ✓
                                    </span>
                                    <span>Spot trends and winning strategies</span>
                                </li>

                            </ul>

                            {/* Stats */}
                            <div className="mt-4 grid grid-cols-3 gap-3">

                                <div className="rounded-2xl bg-white/10 p-4">
                                    <p style={{ color: colors.primary.teal }} className="text-xl font-bold">$248k</p>
                                    <p className="text-xs text-white/70">Monthly revenue</p>
                                </div>

                                <div className="rounded-2xl bg-white/10 p-4">
                                    <p style={{ color: colors.primary.teal }} className="text-xl font-bold">+215%</p>
                                    <p className="text-xs text-white/70">Conversion rate</p>
                                </div>

                                <div className="rounded-2xl bg-white/10 p-4">
                                    <p style={{ color: colors.primary.teal }} className="text-xl font-bold">2,300</p>
                                    <p className="text-xs text-white/70">Qualified leads</p>
                                </div>

                            </div>
                        </div>

                        {/* Right Dashboard Card */}
                        <div className="rounded-3xl bg-white p-4 shadow-xl" >

                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold text-slate-800">
                                    Dashboard Preview
                                </p>

                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                    Live
                                </span>
                            </div>

                            {/* Chart */}
                            <div className="mt-4 rounded-2xl border p-4">
                                <p className="text-sm font-semibold text-slate-800">
                                    Sales Growth
                                </p>

                                <div className="mt-3 h-40 rounded-xl bg-gradient-to-br from-slate-100 to-white flex items-end gap-2 p-3">

                                    <div className="h-12 w-6 rounded-lg bg-indigo-300"></div>
                                    <div className="h-16 w-6 rounded-lg bg-indigo-400"></div>
                                    <div className="h-20 w-6 rounded-lg bg-indigo-500"></div>
                                    <div className="h-24 w-6 rounded-lg bg-indigo-600"></div>
                                    <div className="h-28 w-6 rounded-lg bg-indigo-700"></div>
                                    <div className="h-32 w-6 rounded-lg bg-teal-500"></div>

                                </div>
                            </div>

                            {/* Small Stats */}
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">

                                <div className="rounded-2xl border p-4">
                                    <p className="text-xs text-slate-500">New Leads</p>
                                    <p className="mt-1 text-lg font-bold text-slate-900">52</p>
                                </div>

                                <div className="rounded-2xl border p-4">
                                    <p className="text-xs text-slate-500">Conversion</p>
                                    <p className="mt-1 text-lg font-bold text-slate-900">
                                        21.5%
                                    </p>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </section>



            {/*==============  */}
            {/* Benefits */}
            <section className="py-5"
                style={{ background: "#f0f6ff", }} >
                <Container>
                    {/* SECTION LABEL */}
                    <Row className="mb-4">
                        <Col className="text-center">
                            <span className="fw-semibold text-primary px-3 py-1 rounded-pill"
                                style={{
                                    backgroundColor: "#eef2ff", fontSize: "14px",
                                }} >
                                Benefits
                            </span>

                            <h2
                                className="fw-bold mt-3"
                                style={{
                                    fontSize: "36px",
                                    color: colors.primary.darkBlue
                                }}   >
                                Benefits That Drive Real Sales Growth
                            </h2>

                            <p
                                className="text-muted mt-2 mx-auto"
                                style={{ maxWidth: "700px" }}   >
                                Everything you need to boost productivity, improve collaboration,
                                and accelerate revenue growth — all in one CRM platform.
                            </p>
                        </Col>
                    </Row>

                    {/* BENEFITS CARDS */}
                    <Row className="g-4 justify-content-center">
                        {benefits.map((item, index) => (
                            <Col lg={3} md={6} sm={12} key={index}>
                                <div
                                    className="h-100 bg-white rounded-4 p-4 text-start shadow-sm transition"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                    style={{
                                        border: "2px solid #e5e7eb",
                                        transition: "all 0.3s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = "#2dd4bf";
                                        e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                                        e.currentTarget.style.transform = "translateY(-8px)";

                                        const title = e.currentTarget.querySelector("h6");
                                        if (title) title.style.color = "#2dd4bf";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "#e5e7eb";
                                        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                        const title = e.currentTarget.querySelector("h6");
                                        if (title) title.style.color = "#1e3a8a";
                                    }}
                                >
                                    <div
                                        className="mb-3 d-flex align-items-center justify-content-center rounded-3"
                                        style={{
                                            width: "48px",
                                            height: "48px",
                                            backgroundColor: "#2dd4bf",
                                        }}
                                    >
                                        <FeatherIcon
                                            icon={item.icon}
                                            size={22}
                                            className="text-white"
                                        />
                                    </div>

                                    <h6 className="fw-bold mb-2">{item.title}</h6>
                                    <p className="text-muted mb-0 small">
                                        {item.desc}
                                    </p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>


            <section style={{ paddingTop: "90px", paddingBottom: "90px" }}>
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-4 mb-lg-0">
                            <span
                                className="fw-semibold text-primary mb-2 d-inline-block"
                                style={{ fontSize: "14px" }}
                            >
                                FAQs
                            </span>

                            <h2 className="fw-bold mb-4" style={{ fontSize: "42px", color: colors.primary.darkBlue }}>
                                Your Questions, Answered.
                            </h2>

                            <Accordion defaultActiveKey="0" flush>
                                {[
                                    {
                                        q: "What is SalesTrackify?",
                                        a: "SalesTrackify is an all-in-one CRM platform designed to help sales teams manage leads, track opportunities, automate follow-ups, and close deals faster."
                                    },
                                    {
                                        q: "How does SalesTrackify help my sales team?",
                                        a: "It centralizes your leads, pipelines, tasks, and reports so your team spends less time on manual work and more time selling."
                                    },
                                    {
                                        q: "Is SalesTrackify easy to use?",
                                        a: "Yes. The platform is built with a clean and intuitive interface that allows teams to get started quickly with minimal training."
                                    },
                                    {
                                        q: "Can I track my sales pipeline in real time?",
                                        a: "Yes, you get real-time visibility into deal stages, conversions, and performance through visual dashboards."
                                    },
                                    {
                                        q: "Does SalesTrackify support automation and reminders?",
                                        a: "Yes. Automated follow-ups, reminders, and alerts ensure you never miss an opportunity."
                                    },
                                    {
                                        q: "Is my data secure with SalesTrackify?",
                                        a: "Absolutely. SalesTrackify follows enterprise-grade security standards to keep your data safe and reliable."
                                    },
                                    {
                                        q: "Can I request a demo before getting started?",
                                        a: "Yes, you can easily book a demo to explore how SalesTrackify fits your sales workflow."
                                    }
                                ].map((item, index) => (
                                    <Accordion.Item eventKey={index.toString()} key={index} style={{ borderBottom: "1px solid #e5e7eb" }}>
                                        <Accordion.Header
                                            className="fw-semibold"
                                            style={{
                                                color: colors.primary.teal,
                                                backgroundColor: "transparent",
                                            }}
                                        >
                                            {item.q}
                                        </Accordion.Header>
                                        <Accordion.Body
                                            style={{
                                                color: colors.primary.darkBlue,
                                                backgroundColor: "#ffffff",
                                                fontSize: "15px",
                                                lineHeight: "1.6",
                                                padding: "1.25rem 1rem"
                                            }}
                                        >
                                            {item.a}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>

                            <style>{`
                    /* Remove any conflicting styles */
                    .accordion-button {
                        color: ${colors.primary.teal} !important;
                        background-color: transparent !important;
                        border: none !important;
                        padding: 1rem !important;
                        box-shadow: none !important;
                    }
                    
                    .accordion-button:not(.collapsed) {
                        color: ${colors.primary.teal} !important;
                        background-color: #f0f9f8 !important;
                        border-color: ${colors.primary.teal} !important;
                    }
                    
                    .accordion-button:focus {
                        border-color: ${colors.primary.teal} !important;
                        box-shadow: none !important;
                    }
                    
                    .accordion-button::after {
                        background-size: 1rem;
                        transition: all 0.3s ease;
                    }
                    
                    /* Fix for accordion body */
                    .accordion-body {
                        color: ${colors.primary.darkBlue} !important;
                        background-color: #ffffff !important;
                        visibility: visible !important;
                        opacity: 1 !important;
                        display: block !important;
                    }
                    
                    /* Ensure accordion collapse works properly */
                    .accordion-collapse {
                        visibility: visible !important;
                    }
                    
                    .accordion-collapse.show {
                        visibility: visible !important;
                    }
                    
                    /* Remove any text-muted that might be hiding content */
                    .text-muted {
                        color: #000000 !important;
                    }
                    
                    /* Override any potential display none issues */
                    .accordion-body.collapse:not(.show) {
                        display: none;
                    }
                    
                    .accordion-body.collapse.show {
                        display: block;
                    }
                `}</style>
                        </Col>

                        {/* RIGHT IMAGE */}
                        <Col lg={6}>
                            <img
                                src={ContactUS}
                                loading="lazy"
                                alt="CoreScripts CEO"
                                className="img-fluid rounded-4 shadow"
                            />
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Built by */}
            <section className="py-5" style={{
                // background: "linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)",
                paddingTop: "80px",
                paddingBottom: "80px",
            }}>
                <div className="container py-5">
                    <Row className="align-items-center">

                        <Col lg={5} className="mb-4 mb-lg-0">
                            <div
                                style={{
                                    // background: "linear-gradient(135deg, #99c0ff, #8ca3ff, #a884ff)",
                                    borderRadius: "20px",
                                    padding: "20px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <img
                                    src={seo}
                                    loading="lazy"
                                    alt="CoreScripts CEO"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        borderRadius: "16px",
                                    }}
                                />
                            </div>
                        </Col>

                        {/* RIGHT SIDE - CONTENT */}
                        <Col lg={7}>
                            <h2
                                className="fw-bold"
                                style={{

                                    fontSize: "42px",
                                    color: colors.primary.teal,
                                    lineHeight: "1.2",
                                }}
                            >
                                Built by <br />
                                <span style={{ color: colors.primary.darkBlue }}> CoreScripts Technologies Ltd</span>
                            </h2>

                            <p className="text-muted fs-5 mt-3" style={{ maxWidth: "650px" }}>
                                SalesTrackify is developed by CoreScripts Technologies Ltd, a UK-based
                                software company delivering bespoke software solutions and full-cycle
                                product engineering services. With a strong focus on reliability,
                                scalability and data security, our team ensures SalesTrackify remains
                                a trusted platform for growing businesses.
                            </p>

                            {/* QUOTE */}
                            <div className="mt-4 p-3 ps-4 border-start"
                                style={{ borderColor: "#3b82f6" }}
                            >
                                <p className="fs-5" style={{ color: "#0f172a" }}>
                                    “Our goal is to build technology that empowers businesses to automate,
                                    scale and grow with confidence — backed by high-quality engineering
                                    and reliable systems.”
                                </p>
                                <h6 className="fw-bold mb-0">CoreScripts Technologies Ltd</h6>
                            </div>
                        </Col>
                    </Row>
                </div>
            </section>
        </>
    );
};


export default Features1;
